import React, { useEffect, useRef, useState } from "react";
import { Clock, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { isMuted } from "./SoundToggle";

export const CALL_IDLE_MS = Number(localStorage.getItem("oryntix_call_idle_ms")) || 3 * 60 * 1000;   // no one spoke this long → the call ends (QA override via localStorage)
export const CALL_WARN_MS = 30 * 1000;       // "Masih di sini?" shown this long before the end
export const IDLE_END_MESSAGE = "Panggilan diakhiri karena tidak ada aktivitas selama 3 menit";

// RMS meter for a remote MediaStream (friend audio) so human-only calls count as active while someone talks.
export function createLevelMeter(stream) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ac = new AC(); const an = ac.createAnalyser(); an.fftSize = 256;
    ac.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    return {
      read() { an.getByteTimeDomainData(buf); let s = 0; for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; s += d * d; } return Math.sqrt(s / buf.length); },
      close() { try { ac.close(); } catch (e) {} },
    };
  } catch (e) { return { read: () => 0, close: () => {} }; }
}

// `isActive()` is polled every second; any truthy result resets the idle clock. Returns { warnLeft, touch, stay }.
export function useCallIdle({ enabled, isActive, onTimeout }) {
  const { user } = useAuth();
  const lastRef = useRef(Date.now());
  const [warnLeft, setWarnLeft] = useState(null);
  const firedRef = useRef(false);
  const cbRef = useRef({ isActive, onTimeout });
  cbRef.current = { isActive, onTimeout };
  const touch = () => { lastRef.current = Date.now(); if (firedRef.current) return; setWarnLeft((w) => (w === null ? w : null)); };
  useEffect(() => {
    if (!enabled) return undefined;
    lastRef.current = Date.now(); firedRef.current = false; setWarnLeft(null);
    const t = setInterval(() => {
      if (firedRef.current) return;
      let act = false;
      try { act = !!cbRef.current.isActive(); } catch (e) {}
      if (act) { touch(); return; }
      const idle = Date.now() - lastRef.current;
      if (idle >= CALL_IDLE_MS) { firedRef.current = true; setWarnLeft(null); toast.message(IDLE_END_MESSAGE); cbRef.current.onTimeout(); return; }
      if (idle >= CALL_IDLE_MS - CALL_WARN_MS) {
        setWarnLeft((w) => { if (w === null && !isMuted(user)) beep(); return Math.max(1, Math.ceil((CALL_IDLE_MS - idle) / 1000)); });
      }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [enabled]);
  return { warnLeft, touch, stay: touch };
}

function beep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext; const ac = new AC();
    const o = ac.createOscillator(); const g = ac.createGain(); o.frequency.value = 880; g.gain.value = 0.08;
    o.connect(g); g.connect(ac.destination); o.start(); o.stop(ac.currentTime + 0.25); setTimeout(() => ac.close(), 400);
    navigator.vibrate?.(120);
  } catch (e) {}
}

export function IdleWarning({ seconds, onStay, onEnd }) {
  if (seconds === null || seconds === undefined) return null;
  return (
    <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm" data-testid="call-idle-warning">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0F1A3A] p-6 text-center text-white shadow-2xl">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/20 text-amber-300"><Clock size={22} /></span>
        <h3 className="mt-4 text-lg font-bold">Masih di sini?</h3>
        <p className="mt-1 text-sm text-white/70">Tidak ada aktivitas selama beberapa menit. Panggilan akan diakhiri dalam <span className="font-mono font-bold text-amber-300" data-testid="call-idle-countdown">{seconds}</span> dtk.</p>
        <div className="mt-5 flex gap-2">
          <button onClick={onStay} data-testid="call-idle-stay" className="flex-1 rounded-xl bg-[#2F6BFF] py-2.5 text-sm font-semibold transition hover:brightness-110">Saya masih di sini</button>
          <button onClick={onEnd} data-testid="call-idle-end" className="flex items-center justify-center gap-1.5 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold transition hover:bg-[#EF4444]"><PhoneOff size={15} /> Akhiri</button>
        </div>
      </div>
    </div>
  );
}
